import React from 'react';
import { X, AlertTriangle, RefreshCw } from 'lucide-react';
import Button from '../ui/Button';
import FocusedDialog from '../ui/FocusedDialog';
import terminateService, {
  TerminateRequest, TerminatePreview,
} from '../../services/terminateService';

interface ConfirmTerminateDialogProps {
  isOpen: boolean;
  /** Scope + selector fields (scope/position_ids/basket_id/strategy_id/
   *  broker/client_id). `reason`/`dry_run` are added by the dialog. */
  request: TerminateRequest | null;
  /** Human label for the scope, e.g. "this strategy", "the NIFTY basket",
   *  "Zerodha · AB1234". Rendered in the title/body. */
  scopeLabel?: string;
  onClose: () => void;
  /** Parent runs the real terminate (so it owns the toast + refetch). */
  onConfirm: (reason: string) => void | Promise<void>;
}

const CONFIRM_WORD = 'TERMINATE';

/**
 * Confirmation for Terminate — a platform-only "stop watching" action that
 * never calls the broker. Because the schedule/positions view is date-scoped,
 * the in-scope count is fetched from the backend via a dry_run so wide scopes
 * (STRATEGY/BASKET/ACCOUNT/ALL) can't under-count positional / other-account
 * rows. The ALL scope is gated behind a typed confirmation.
 */
const ConfirmTerminateDialog: React.FC<ConfirmTerminateDialogProps> = ({
  isOpen,
  request,
  scopeLabel,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = React.useState('');
  const [typed, setTyped] = React.useState('');
  const [preview, setPreview] = React.useState<TerminatePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  // Ref mirror of `submitting` so a second click landing in the same tick
  // (before the setSubmitting re-render commits) is rejected — the render
  // closure's `submitting` is still false there and would let a duplicate
  // terminate POST through.
  const submittingRef = React.useRef(false);

  const isAll = request?.scope === 'ALL';

  // Fetch the authoritative in-scope count whenever the dialog opens for a
  // request. Reset all local state on open/close so a reused dialog instance
  // never leaks a prior reason / typed word / stale count.
  React.useEffect(() => {
    if (!isOpen || !request) {
      setReason('');
      setTyped('');
      setPreview(null);
      setPreviewError(null);
      setSubmitting(false);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    setPreview(null);
    terminateService
      .previewTerminate(request)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((e) => { if (!cancelled) setPreviewError(e?.message || 'Failed to load count'); })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [isOpen, request]);

  if (!isOpen || !request) return null;

  const count = preview?.terminated_count ?? 0;
  const chains = preview?.chains_to_disarm ?? 0;
  const typedOk = !isAll || typed.trim().toUpperCase() === CONFIRM_WORD;
  // Nothing in scope → the confirm is a no-op. Only treat it as empty once the
  // preview has actually resolved: while loading, `count` defaults to 0 and
  // must NOT be mistaken for an empty scope. Chains-to-disarm still count as
  // actionable even when zero positions terminate. When the scope is genuinely
  // empty we collapse the dialog to a plain "nothing to do" state + a single
  // Close button, rather than a faded (disabled) danger button that reads as
  // clickable — this is the one place that knows the authoritative count for
  // every scope (SINGLE / STRATEGY / BASKET / ACCOUNT / ALL).
  const previewReady = !loadingPreview && !previewError && preview !== null;
  const nothingToDo = previewReady && count === 0 && chains === 0;
  const confirmDisabled =
    loadingPreview || submitting || !typedOk || !!previewError || nothingToDo;

  const handleConfirm = async () => {
    if (confirmDisabled || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm(reason.trim());
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const scopeText = scopeLabel ? ` in ${scopeLabel}` : '';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <FocusedDialog
        ariaLabel="Terminate positions"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full animate-in fade-in duration-200 zoom-in-95"
      >
        <div className="flex items-start gap-4 p-6 pb-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Terminate positions
            </h3>

            <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed space-y-2">
              {loadingPreview ? (
                <p className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <RefreshCw className="h-4 w-4 animate-spin" /> Counting positions…
                </p>
              ) : previewError ? (
                <p className="text-red-600 dark:text-red-400">{previewError}</p>
              ) : nothingToDo ? (
                <p>
                  The platform isn’t managing any positions{scopeText} — there’s
                  nothing to terminate.
                </p>
              ) : (
                <p>
                  This stops the platform from managing{' '}
                  <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
                    {count}
                  </span>{' '}
                  position{count === 1 ? '' : 's'}{scopeText}
                  {chains > 0 && (
                    <> and disarms <span className="font-semibold tabular-nums">{chains}</span> re-entry/TP chain{chains === 1 ? '' : 's'}</>
                  )}.
                </p>
              )}

              {/* Warning + reason + typed-gate are irrelevant when there is
                  nothing to terminate — collapse to the empty-state message. */}
              {!nothingToDo && (
                <>
                  <p className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg p-3">
                    Open positions and pending orders at your broker will remain —
                    <span className="font-semibold"> you are responsible for them.</span>{' '}
                    Terminate never places a broker order and cannot be undone.
                  </p>

                  {/* Optional reason */}
                  <div>
                    <label htmlFor="terminate-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Reason <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      id="terminate-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      placeholder="e.g. closed manually in the broker terminal"
                      className="w-full rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Typed gate for ALL */}
                  {isAll && (
                    <div>
                      <label htmlFor="terminate-confirm-word" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                        Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span> to confirm terminating your entire book
                      </label>
                      <input
                        id="terminate-confirm-word"
                        type="text"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        autoComplete="off"
                        className="w-full rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-shrink-0 w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 pb-6">
          {nothingToDo ? (
            // Empty scope → the only meaningful action is to dismiss. A single
            // Close button avoids offering a faded (disabled) Terminate that
            // still reads as clickable.
            <Button variant="primary" onClick={onClose} className="flex-1 whitespace-nowrap">
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={submitting} className="flex-1 whitespace-nowrap">
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirm}
                disabled={confirmDisabled}
                className="flex-1 whitespace-nowrap"
              >
                {submitting ? 'Terminating…' : `Terminate${count ? ` ${count}` : ''}`}
              </Button>
            </>
          )}
        </div>
      </FocusedDialog>
    </div>
  );
};

export default ConfirmTerminateDialog;
